import React, { ReactNode } from 'react';
import { Box, Typography, Stack, Card, CardContent } from '@mui/material';
import IconBox from '@/components/common/IconBox';

export interface InfoCardData {
    icon: ReactNode;
    iconColor?: string;
    title: string;
    content: ReactNode;
    useIconBox?: boolean;
}

export type InfoCardsLayout = 'centered' | 'stacked';

interface InfoCardsProps {
    cards: InfoCardData[];
    layout?: InfoCardsLayout;
    stackSpacing?: { xs: number; sm?: number; md: number; lg?: number };
    containerMargin?: { xs: number; sm?: number; md: number; lg?: number };
  
    onCardClick?: (index: number) => void;
    selectedIndex?: number;
    animation?: {
        /** translateY on hover in px */
        hover?: number;
        /** translateY when selected in px */
        selected?: number;
        /** duration in ms */
        duration?: number;
        /** easing function */
        easing?: string;
    };
}

const InfoCards: React.FC<InfoCardsProps> = ({
    cards,
    layout = 'stacked',
    stackSpacing = { xs: 2, sm: 2.5, md: 3 },
    containerMargin = { xs: 3, sm: 4, md: 6, lg: 8 },
    onCardClick,
    selectedIndex,
    animation
}) => {
    const isCenteredLayout = layout === 'centered';

    const renderCard = (card: InfoCardData, index: number) => {
        const {
            icon,
            iconColor = 'secondary.main',
            title,
            content,
            useIconBox = isCenteredLayout
        } = card;
        const isSelected = typeof selectedIndex === 'number' && selectedIndex === index

        const defaultAnim = { hover: 6, selected: 8, duration: 250, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' };
        const anim = { ...defaultAnim, ...(animation ?? {}) };

        return (
            <Box
                key={index}
                sx={{
                    flex: isCenteredLayout ? 1 : 1,
                    display: 'flex',
                    flexDirection: isCenteredLayout ? 'row' : 'column',
                    justifyItems: isCenteredLayout
                        ? index % 2 === 0
                            ? 'end'
                            : 'start'
                        : undefined,
                    p: 4
                }}
            >
                <Card
                    elevation={0}
                    sx={{
                        border: '1px solid #E0E0E0',
                        cursor: onCardClick ? 'pointer' : 'default',
                        ...(isSelected && {
                            borderColor: 'primary.main',
                            boxShadow: 3
                        }),
                        transition: `transform ${anim.duration}ms ${anim.easing}, box-shadow ${anim.duration}ms ${anim.easing}`,
                        transform: isSelected ? `translateY(-${anim.selected}px)` : undefined,
                        '&:hover': onCardClick ? { transform: `translateY(-${anim.hover}px)`, boxShadow: 6 } : undefined,
                        '@media (prefers-reduced-motion: reduce)': {
                            transition: 'none',
                            transform: 'none',
                            '&:hover': { transform: 'none', boxShadow: 'none' }
                        },
                        borderRadius: 2,
                        height: '100%',
                        ...(isCenteredLayout && {
                            width: { xs: '100%', md: '80%', lg: '70%', xl: '60%' }
                        }),
                        ...(!isCenteredLayout && { flex: 1 })
                    }}
                    onClick={() => onCardClick?.(index)}
                    role={onCardClick ? 'button' : undefined}
                    tabIndex={onCardClick ? 0 : undefined}
                    onKeyDown={(e) => {
                        if (!onCardClick) return
                        if (e.key === 'Enter' || e.key === ' ') {
                            onCardClick(index)
                        }
                    }}
                >
                    <CardContent
                        sx={{
                            p: { xs: 2, sm: 2.5, md: 3, lg: isCenteredLayout ? 5 : 4 },
                            ...(isCenteredLayout && {
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center'
                            }),
                            ...(!isCenteredLayout && {
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center'
                            })
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                mb: isCenteredLayout ? 2 : { xs: 1.5, sm: 2 }
                            }}
                        >
                            {useIconBox ? (
                                <IconBox boxColor={iconColor}>
                                    {React.cloneElement(icon as React.ReactElement, {
                                        sx: {
                                            fontSize: { xs: 32, md: 36, lg: 40 },
                                            color: 'white'
                                        }
                                    })}
                                </IconBox>
                            ) : (
                                <Box
                                    sx={{
                                        width: { xs: 44, sm: 48, md: 50 },
                                        height: { xs: 44, sm: 48, md: 50 },
                                        borderRadius: 2,
                                        backgroundColor: iconColor,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mb: { xs: 1.5, sm: 2 }
                                    }}
                                >
                                    {React.cloneElement(icon as React.ReactElement, {
                                        sx: {
                                            color: 'white',
                                            fontSize: { xs: 24, sm: 26, md: 28 }
                                        }
                                    })}
                                </Box>
                            )}

                            <Typography
                                variant="h6"
                                fontWeight={600}
                                gutterBottom={!isCenteredLayout}
                                sx={{
                                    fontSize: {
                                        xs: '1rem',
                                        sm: isCenteredLayout ? '1.125rem' : '1.1rem',
                                        md: '1.25rem',
                                        lg: isCenteredLayout ? '1.25rem' : '1.25rem'
                                    },
                                    textAlign: 'center',
                                    color: isCenteredLayout ? 'inherit' : 'primary.dark'
                                }}
                            >
                                {title}
                            </Typography>
                        </Box>

                        {typeof content === 'string' ? (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    fontSize: { xs: '0.8rem', md: '0.875rem' },
                                    textAlign: { xs: 'left', md: isCenteredLayout ? 'left' : 'center' }
                                }}
                            >
                                {content}
                            </Typography>
                        ) : (
                            content
                        )}
                    </CardContent>
                </Card>
            </Box>
        );
    };

    return (
        <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={stackSpacing}
            sx={{
                mb: containerMargin,
                alignItems: 'stretch',
                ...(cards.length < 3 && !isCenteredLayout && {
                    justifyContent: { md: 'center' },
                    px: { md: 4, lg: 16 , xl: 20 }
                }),
                ...(isCenteredLayout && { mx: { xs: 0, md: 4, lg: 20 } })
            }}
        >
            {cards.map((card, index) => renderCard(card, index))}
        </Stack>
    );
};

export default InfoCards;
